import {
  Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit,
  SimpleChanges, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChatService, ChatMessage, ChatContextType } from '../../../services/api/chat';
import { ChatSocketService } from '../../../services/api/chat-socket';
import { AuthService } from '../../../services/auth/auth';
import { FileService, FileDto } from '../../../services/api/file';
import { LanguageService } from '../../../services/i18n/language';
import { ConfirmDialogService } from '../../../services/confirm-dialog';

const AVATAR_PALETTE = ['#0f6e56', '#185fa5', '#993c1d', '#72243e', '#3b6d11', '#854f0b'];

/**
 * Чат заявки или сделки — один компонент для обоих случаев (view-request и
 * view-deal), различие в contextType/contextId и (для заявки, у владельца/брокера)
 * forwarderId. См. ChatController.java: чат заявки — отдельный приватный тред
 * владелец↔конкретный экспедитор (экспедитор передаёт forwarderId не должен — его тред
 * определяется по токену; владелец/брокер обязаны передать forwarderId — какой тред
 * смотрят). Чат сделки — один общий тред (владелец + экспедитор именно этой сделки).
 * Обновления — через STOMP (ChatSocketService), без поллинга.
 */
@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, OnChanges, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  @Input({ required: true }) contextType!: ChatContextType;
  @Input({ required: true }) contextId!: number;
  /** Для REQUEST у владельца/брокера — какой тред (с каким экспедитором) смотрим.
   *  У экспедитора не задаётся — его тред определяется на бэкенде по токену. */
  @Input() forwarderId?: number;
  /** Заявка закрыта для новых сообщений (по ней уже заключена сделка) — история читается,
   *  но композер скрыт; продолжение переписки — в чате сделки. */
  @Input() locked = false;

  @ViewChild('scrollAnchor') private scrollAnchor?: ElementRef<HTMLDivElement>;
  @ViewChild('composerTextarea') private composerTextarea?: ElementRef<HTMLTextAreaElement>;

  messages: ChatMessage[] = [];
  loading = true;
  loadError: string | null = null;
  hasMore = false;
  loadingMore = false;
  private page = 0;

  currentUserId: number | null = null;
  private isBroker = false;
  private isForwarderViewer = false;

  draft = '';
  selectedFiles: File[] = [];
  sending = false;
  sendError: string | null = null;

  replyTarget: ChatMessage | null = null;

  editingMessageId: number | null = null;
  editDraft = '';

  openMenuMessageId: number | null = null;

  private socketSub?: Subscription;
  private avatarColors = new Map<number, string>();

  // Тексты (i18n)
  titleText = 'Чат';
  loadingText = 'Загрузка...';
  emptyText = 'Сообщений пока нет';
  loadErrorText = 'Не удалось загрузить сообщения';
  loadMoreText = 'Показать более старые';
  placeholderText = 'Написать сообщение...';
  replyText = 'Ответить';
  replyingToText = 'Ответ на сообщение';
  cancelText = 'Отмена';
  editText = 'Редактировать';
  deleteText = 'Удалить';
  saveText = 'Сохранить';
  deletedText = 'Сообщение удалено';
  editedText = 'изменено';
  attachFileText = 'Прикрепить файл';
  confirmDeleteText = 'Удалить сообщение?';
  originalDeletedText = 'Исходное сообщение удалено';
  sendErrorText = 'Не удалось отправить сообщение';
  todayText = 'Сегодня';
  messageActionsText = 'Действия с сообщением';
  sendLabelText = 'Отправить';
  lockedHintText = 'Чат заявки закрыт — по ней уже заключена сделка. Переписка продолжается в чате сделки.';

  constructor(
    private chatService: ChatService,
    private chatSocket: ChatSocketService,
    private authService: AuthService,
    private fileService: FileService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.authService.getUser()?.id ?? null;
    this.isBroker = this.authService.getUser()?.role?.code === 'ROLE_CUSTOMS_BROKER';
    this.isForwarderViewer = this.authService.getUser()?.role?.code === 'ROLE_FREIGHT_FORWARDER';
    this.updateTranslations();
    this.languageService.getTranslations().subscribe(() => {
      this.updateTranslations();
      this.cdr.markForCheck();
    });
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const relevantChange = changes['contextId'] || changes['contextType'] || changes['forwarderId'];
    if (relevantChange && !relevantChange.firstChange) {
      this.messages = [];
      this.page = 0;
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.openMenuMessageId = null;
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('chat.title') || this.titleText;
    this.loadingText = this.languageService.translate('chat.loading') || this.loadingText;
    this.emptyText = this.languageService.translate('chat.empty') || this.emptyText;
    this.loadErrorText = this.languageService.translate('chat.loadError') || this.loadErrorText;
    this.loadMoreText = this.languageService.translate('chat.loadMore') || this.loadMoreText;
    this.placeholderText = this.languageService.translate('chat.placeholder') || this.placeholderText;
    this.replyText = this.languageService.translate('chat.reply') || this.replyText;
    this.replyingToText = this.languageService.translate('chat.replyingTo') || this.replyingToText;
    this.cancelText = this.languageService.translate('chat.cancel') || this.cancelText;
    this.editText = this.languageService.translate('chat.edit') || this.editText;
    this.deleteText = this.languageService.translate('chat.delete') || this.deleteText;
    this.saveText = this.languageService.translate('chat.save') || this.saveText;
    this.deletedText = this.languageService.translate('chat.deleted') || this.deletedText;
    this.editedText = this.languageService.translate('chat.edited') || this.editedText;
    this.attachFileText = this.languageService.translate('chat.attachFile') || this.attachFileText;
    this.confirmDeleteText = this.languageService.translate('chat.confirmDelete') || this.confirmDeleteText;
    this.originalDeletedText = this.languageService.translate('chat.originalDeleted') || this.originalDeletedText;
    this.sendErrorText = this.languageService.translate('chat.sendError') || this.sendErrorText;
    this.todayText = this.languageService.translate('chat.today') || this.todayText;
    this.messageActionsText = this.languageService.translate('chat.messageActions') || this.messageActionsText;
    this.sendLabelText = this.languageService.translate('chat.send') || this.sendLabelText;
    this.lockedHintText = this.languageService.translate('chat.lockedHint') || this.lockedHintText;
  }

  /** Для экспедитора, смотрящего REQUEST-чат, — свой собственный тред (без явного @Input). */
  private get effectiveForwarderId(): number | undefined {
    if (this.forwarderId != null) return this.forwarderId;
    if (this.contextType === 'REQUEST' && this.isForwarderViewer) return this.currentUserId ?? undefined;
    return undefined;
  }

  private load(): void {
    this.loading = true;
    this.loadError = null;
    this.chatService.getMessages(this.contextType, this.contextId, 0, 50, this.effectiveForwarderId).subscribe({
      next: (res) => {
        this.messages = res.content;
        this.page = res.number;
        this.hasMore = !res.first;
        this.loading = false;
        this.cdr.markForCheck();
        setTimeout(() => this.scrollToBottom(), 0);
        this.connectSocket();
      },
      error: () => {
        this.loadError = this.loadErrorText;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  loadMore(): void {
    if (this.loadingMore || this.page === 0) return;
    this.loadingMore = true;
    this.chatService.getMessages(this.contextType, this.contextId, this.page - 1, 50, this.effectiveForwarderId).subscribe({
      next: (res) => {
        this.messages = [...res.content, ...this.messages];
        this.page = res.number;
        this.hasMore = !res.first;
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingMore = false;
        this.cdr.markForCheck();
      }
    });
  }

  private connectSocket(): void {
    this.socketSub?.unsubscribe();
    this.socketSub = this.chatSocket.watch(this.contextType, this.contextId, this.effectiveForwarderId, this.isBroker).subscribe({
      next: (event) => {
        const index = this.messages.findIndex(m => m.id === event.message.id);
        if (event.type === 'CREATED') {
          if (index === -1) {
            this.messages = [...this.messages, event.message];
            setTimeout(() => this.scrollToBottom(), 0);
          }
        } else {
          if (index !== -1) {
            const copy = [...this.messages];
            copy[index] = event.message;
            this.messages = copy;
          }
        }
        this.cdr.markForCheck();
      },
      error: () => {
        // соединение оборвалось — переподключаемся при следующей загрузке страницы;
        // для чата это не критично, история всё равно грузится через REST
      }
    });
  }

  private scrollToBottom(): void {
    this.scrollAnchor?.nativeElement.scrollIntoView({ block: 'end' });
  }

  isOwn(message: ChatMessage): boolean {
    return this.currentUserId != null && message.sender.id === this.currentUserId;
  }

  senderLabel(sender: { fullName: string; companyName?: string }): string {
    return sender.companyName || sender.fullName;
  }

  initials(sender: { fullName: string; companyName?: string }): string {
    const name = this.senderLabel(sender).trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  avatarColor(senderId: number): string {
    let color = this.avatarColors.get(senderId);
    if (!color) {
      color = AVATAR_PALETTE[this.avatarColors.size % AVATAR_PALETTE.length];
      this.avatarColors.set(senderId, color);
    }
    return color;
  }

  isNewDay(index: number): boolean {
    if (index === 0) return true;
    const prev = new Date(this.messages[index - 1].createdAt);
    const curr = new Date(this.messages[index].createdAt);
    return prev.toDateString() !== curr.toDateString();
  }

  dayLabel(iso: string): string {
    const date = new Date(iso);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return this.todayText;
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', { day: 'numeric', month: 'long' });
  }

  toggleMenu(event: Event, message: ChatMessage): void {
    event.stopPropagation();
    this.openMenuMessageId = this.openMenuMessageId === message.id ? null : message.id;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
    }
    input.value = '';
  }

  removeSelectedFile(index: number): void {
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
  }

  startReply(message: ChatMessage): void {
    this.replyTarget = message;
    this.openMenuMessageId = null;
    setTimeout(() => this.composerTextarea?.nativeElement.focus(), 0);
  }

  cancelReply(): void {
    this.replyTarget = null;
  }

  canSend(): boolean {
    return !this.sending && (this.draft.trim().length > 0 || this.selectedFiles.length > 0);
  }

  send(): void {
    if (!this.canSend()) return;
    this.sending = true;
    this.sendError = null;
    const body = this.draft.trim() || null;
    const replyToMessageId = this.replyTarget?.id;
    const files = this.selectedFiles;

    this.chatService.sendMessage(this.contextType, this.contextId, body, replyToMessageId, files, this.effectiveForwarderId).subscribe({
      next: (message) => {
        if (!this.messages.some(m => m.id === message.id)) {
          this.messages = [...this.messages, message];
        }
        this.draft = '';
        this.selectedFiles = [];
        this.replyTarget = null;
        this.sending = false;
        this.cdr.markForCheck();
        setTimeout(() => this.scrollToBottom(), 0);
      },
      error: () => {
        this.sendError = this.sendErrorText;
        this.sending = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDraftKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  startEdit(message: ChatMessage): void {
    this.editingMessageId = message.id;
    this.editDraft = message.body || '';
    this.openMenuMessageId = null;
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editDraft = '';
  }

  saveEdit(message: ChatMessage): void {
    const body = this.editDraft.trim();
    if (!body) return;
    this.chatService.editMessage(message.id, body).subscribe({
      next: (updated) => {
        const index = this.messages.findIndex(m => m.id === updated.id);
        if (index !== -1) {
          const copy = [...this.messages];
          copy[index] = updated;
          this.messages = copy;
        }
        this.editingMessageId = null;
        this.cdr.markForCheck();
      }
    });
  }

  async deleteMessage(message: ChatMessage): Promise<void> {
    this.openMenuMessageId = null;
    if (!await this.confirmDialog.confirm(this.confirmDeleteText)) return;
    this.chatService.deleteMessage(message.id).subscribe({
      next: () => {
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          const copy = [...this.messages];
          copy[index] = { ...copy[index], deleted: true, body: null };
          this.messages = copy;
        }
        this.cdr.markForCheck();
      }
    });
  }

  downloadFile(file: FileDto): void {
    this.fileService.downloadFile(file.uid, file.fileName);
  }

  formatTime(iso: string): string {
    const date = new Date(iso);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleTimeString(lang === 'en' ? 'en-US' : 'ru-RU', { hour: '2-digit', minute: '2-digit' });
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  }
}
